import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from '../../utils/axios';
import { withRequestDedupe, SINGLETON_KEY } from '../requestDedupe';
import { showAlert } from './alertSlice';
import { updateUiState } from 'redux/slices/uiSlice';

const teamMembersInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  members: null,
  emptyList: null,
  lastSeen: null,
};

const invitationsInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  invitations: null,
  emptyList: null,
  lastSeen: null,
};

const orgConfigsInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  orgConfigs: null,
};

const teamsInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  teams: null,
  emptyList: null,
  lastSeen: null,
};

const sellerParticipantsInitialState = {
  fetching: false,
  fetchingFailed: false,
  data: [],
};

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};

const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  teamMembers: { ...teamMembersInitialState },
  invitations: { ...invitationsInitialState },
  orgConfigs: { ...orgConfigsInitialState },
  teams: { ...teamsInitialState },
  sellerParticipants: { ...sellerParticipantsInitialState },
  addRemoveTeamMember: { ...mutationInitialState },
  updateTeamPermissions: { ...mutationInitialState },
  sendTeamInvite: { ...mutationInitialState },
  createOrgSeller: { ...mutationInitialState },
  deleteOrgSeller: { ...mutationInitialState },
  updateOrgSeller: { ...mutationInitialState },
  updateOrgConfig: { ...mutationInitialState },
  mapCrmUser: { ...mutationInitialState },
  mapHubspotUser: { ...mutationInitialState },
  mapZohoUser: { ...mutationInitialState },
  updateTrackedCrmDeals: { ...mutationInitialState },
  updateCrmDeal: { ...mutationInitialState },
  crmDealContacts: { ...fetchDataInitialState },
  crmDeals: { ...fetchDataInitialState },
  crmDealsV2: { ...fetchDataInitialState },
  crmProperties: { ...fetchDataInitialState },
  crmUsers: { ...fetchDataInitialState },
  hubspotCompanies: { ...fetchDataInitialState },
  hubspotUsers: { ...fetchDataInitialState },
  zohoUsers: { ...fetchDataInitialState },
  interactionCounts: { ...fetchDataInitialState },
  orgData: { ...fetchDataInitialState },
};

// --- Async Thunks ---

export const fetchTeamMembers = createAsyncThunk(
  'org/fetchTeamMembers',
  async (
    { teamId, wlOrgId, fetchCounts, pastNDays, size, lastSeen },
    { dispatch, rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(`/us/org/members`, {
              params: {
                'request-id': reqId,
                lastSeen: lastSeen,
                limit: size,
                teamId: teamId,
                wlCustomerId: wlOrgId,
                fetchCounts,
                pastNDays,
              },
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.status === 200) {
              resolve({
                data: response.data,
                lastSeen,
                responseDataLastSeen:
                  response?.data?.[response?.data?.length - 1]?.createdOn,
              });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch team members'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch team members'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchInvitations = createAsyncThunk(
  'org/fetchInvitations',
  async (
    { invitationType, orgId, size, lastSeen },
    { dispatch, rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(`/nf/org/invitations`, {
              params: {
                'request-id': reqId,
                last_seen: lastSeen,
                size: size,
                invitationType: invitationType || 'team_invite',
                teamId: orgId,
              },
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.status === 200) {
              resolve({
                data: response.data.invitations,
                lastSeenArg: lastSeen,
                responseDataLastSeen: response?.data?.last_seen,
              });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch team invitations'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch team invitations'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchOrganizationConfigs = createAsyncThunk(
  'org/fetchOrganizationConfigs',
  // Argument-less and dispatched from 67 sites. Concurrent dispatches share
  // one request - see `redux/requestDedupe`.
  withRequestDedupe(SINGLETON_KEY, async (_, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(`/us/org/configs`, {
              params: {
                'request-id': reqId,
              },
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.status === 200) {
              dispatch(
                updateUiState({
                  key: 'unassociatedItemsCount',
                  value: response?.data?.orgCounts?.unAssociatedItemsHigh,
                }),
              );
              resolve(response.data);
            } else {
              reject(rejectWithValue('Failed to fetch org configs'));
            }
          } catch (e) {
            reject(rejectWithValue('Failed to fetch org configs'));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  }),
);

export const fetchOrgData = createAsyncThunk(
  'org/fetchOrgData',
  async (_, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/org-data`, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to fetch org data'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchTeams = createAsyncThunk(
  'org/fetchTeams',
  async ({ size, lastSeen }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(`/us/teams`, {
              params: {
                'request-id': reqId,
                limit: size,
                lastSeen,
              },
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.status === 200) {
              resolve({
                data: response.data,
                lastSeenArg: lastSeen,
                responseDataLastSeen:
                  response?.data?.[response?.data?.length - 1]?.id,
              });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to fetch teams'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to fetch teams'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Oops! Something went wrong. Please try again.',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const updateTeam = createAsyncThunk(
  'org/updateTeam',
  async ({ teamId, data }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.put(`/us/teams/${teamId}`, data, {
              params: {
                'request-id': reqId,
              },
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.status === 200) {
              resolve({ id: teamId, data: response.data });
            } else {
              dispatch(
                showAlert({
                  message: 'Failed to update team details',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to update team details'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to update team details',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to update team details'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Failed to update team details',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const deleteTeam = createAsyncThunk(
  'org/deleteTeam',
  async (teamId, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.delete(`/us/teams/${teamId}`, {
              params: {
                'request-id': reqId,
              },
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.status === 200) {
              resolve(teamId);
            } else {
              dispatch(
                showAlert({
                  message: 'Failed to delete team',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to delete team'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to delete team',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue('Failed to delete team'));
          }
        },
        (error) => {
          dispatch(
            showAlert({
              message: 'Failed to delete team',
              showCross: true,
              type: 'error',
              autoHideDuration: 2000,
            }),
          );
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchOrgSellerParticipants = createAsyncThunk(
  'org/fetchOrgSellerParticipants',
  async (_, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(
              `/us/qai/org/seller-participants`,
              {
                params: {
                  'request-id': reqId,
                },
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            if (response.status === 200) {
              resolve(response?.data);
            } else {
              reject(rejectWithValue('Failed to get org seller participants'));
            }
          } catch (e) {
            reject(rejectWithValue('Failed to get org seller participants'));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const addRemoveTeamMember = createAsyncThunk(
  'org/addRemoveTeamMember',
  async ({ action, teamId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user)
            return reject(rejectWithValue({ text: null, is400: false }));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/team/${teamId}/members/add-remove`,
              payload,
              {
                params: { 'request-id': uuidv4(), action },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else
              reject(
                rejectWithValue({ text: response.data?.text, is400: false }),
              );
          } catch (e) {
            if (
              e?.response?.status === 400 &&
              e?.response?.data?.status === 400
            ) {
              reject(
                rejectWithValue({ text: e?.response?.data?.text, is400: true }),
              );
            } else {
              reject(rejectWithValue({ text: null, is400: false }));
            }
          }
        },
        (error) => reject(rejectWithValue({ text: null, is400: false })),
      );
    });
  },
);

export const updateTeamPermissions = createAsyncThunk(
  'org/updateTeamPermissions',
  async ({ teamId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user)
            return reject(rejectWithValue({ text: null, is400: false }));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/team/${teamId}/permissions`,
              payload,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else
              reject(
                rejectWithValue({ text: response.data?.text, is400: false }),
              );
          } catch (e) {
            if (
              e?.response?.status === 400 &&
              e?.response?.data?.status === 400
            ) {
              reject(
                rejectWithValue({ text: e?.response?.data?.text, is400: true }),
              );
            } else {
              reject(rejectWithValue({ text: null, is400: false }));
            }
          }
        },
        (error) => reject(rejectWithValue({ text: null, is400: false })),
      );
    });
  },
);

export const sendTeamInvite = createAsyncThunk(
  'org/sendTeamInvite',
  async (payload, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user)
            return reject(rejectWithValue({ text: null, is400: false }));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(`/nf/invite`, payload, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else
              reject(
                rejectWithValue({
                  text: response?.response?.data?.message,
                  is400: false,
                }),
              );
          } catch (e) {
            if (
              e?.response?.status === 400 &&
              e?.response?.data?.status === 400
            ) {
              reject(
                rejectWithValue({
                  text: e?.response?.data?.message,
                  is400: true,
                }),
              );
            } else {
              reject(
                rejectWithValue({
                  text: e?.response?.data?.message,
                  is400: false,
                }),
              );
            }
          }
        },
        (error) =>
          reject(
            rejectWithValue({
              text: error?.response?.data?.message,
              is400: false,
            }),
          ),
      );
    });
  },
);

export const createOrgSellerParticipant = createAsyncThunk(
  'org/createOrgSellerParticipant',
  async (requestDto, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/org/seller-participant`,
              requestDto,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to create org seller'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const deleteOrgSellerParticipant = createAsyncThunk(
  'org/deleteOrgSellerParticipant',
  async (sellerId, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.delete(
              `/us/qai/org/seller-participant/${sellerId}`,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(sellerId);
            else reject(rejectWithValue('Failed to delete org seller'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateOrgSellerParticipant = createAsyncThunk(
  'org/updateOrgSellerParticipant',
  async ({ sellerId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/org/seller-participant/${sellerId}`,
              payload,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to update org seller'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateOrgConfig = createAsyncThunk(
  'org/updateOrgConfig',
  async (payload, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue(null));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(`/us/organization`, payload, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(null)),
      );
    });
  },
);

export const mapCrmUser = createAsyncThunk(
  'org/mapCrmUser',
  async ({ tool, payload }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(`/us/qai/map-users`, payload, {
              params: { 'request-id': uuidv4(), service: tool },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message:
                    'Failed to map crm user to team member. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to map crm user to team member'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message:
                  'Failed to map crm user to team member. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const mapHubspotUser = createAsyncThunk(
  'org/mapHubspotUser',
  async (payload, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(`/us/hb/map-users`, payload, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message:
                    'Failed to map hubspot user to team member. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(
                rejectWithValue('Failed to map hubspot user to team member'),
              );
            }
          } catch (e) {
            dispatch(
              showAlert({
                message:
                  'Failed to map hubspot user to team member. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const mapZohoUser = createAsyncThunk(
  'org/mapZohoUser',
  async (payload, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(`/us/zh/map-users`, payload, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message:
                    'Failed to map zoho user to team member. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to map zoho user to team member'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message:
                  'Failed to map zoho user to team member. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateTrackedCrmDeals = createAsyncThunk(
  'org/updateTrackedCrmDeals',
  async (payload, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue(null));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/crm/deals/trackV2`,
              payload,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(null)),
      );
    });
  },
);

export const fetchCrmDealContacts = createAsyncThunk(
  'org/fetchCrmDealContacts',
  async (dealId, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/us/qai/deals/${dealId}/contacts`,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Failed to get crm deal contacts. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to get crm deal contacts'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to get crm deal contacts. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchCrmDeals = createAsyncThunk(
  'org/fetchCrmDeals',
  async (payload, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue(null));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(`/us/qai/deals/list`, payload, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(null)),
      );
    });
  },
);

export const fetchCrmDealsV2 = createAsyncThunk(
  'org/fetchCrmDealsV2',
  async (fetchAll, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue(null));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/qai/crm/deals`, {
              params: { 'request-id': uuidv4(), fetchAll },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(null)),
      );
    });
  },
);

export const fetchCrmProperties = createAsyncThunk(
  'org/fetchCrmProperties',
  async (sync, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue(null));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/qai/deals/properties`, {
              params: { 'request-id': uuidv4(), sync },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(null)),
      );
    });
  },
);

export const fetchCrmUsers = createAsyncThunk(
  'org/fetchCrmUsers',
  async (service, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/qai/users`, {
              params: { 'request-id': uuidv4(), service },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Failed to get crm users. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to get crm users'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to get crm users. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchHubspotCompanies = createAsyncThunk(
  'org/fetchHubspotCompanies',
  async (_, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/hb/companies`, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Failed to get hubspot companies. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to get hubspot companies'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to get hubspot companies. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchHubspotUsers = createAsyncThunk(
  'org/fetchHubspotUsers',
  async (_, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/hb/users`, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Failed to get hubspot users. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to get hubspot users'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to get hubspot users. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchZohoUsers = createAsyncThunk(
  'org/fetchZohoUsers',
  async (_, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/zh/users`, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Failed to get zoho users. Please try again!',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to get zoho users'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Failed to get zoho users. Please try again!',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateCrmDeal = createAsyncThunk(
  'org/updateCrmDeal',
  async ({ dealId, saleReviewId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue(null));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/deals/crm/${dealId}`,
              payload,
              {
                params: { 'request-id': uuidv4(), saleReviewId },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue(response?.response?.data));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data));
          }
        },
        (error) => reject(rejectWithValue(null)),
      );
    });
  },
);

export const getInteractionCounts = createAsyncThunk(
  'org/getInteractionCounts',
  async (_, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(`/us/interactions/counts`, {
              params: { 'request-id': uuidv4() },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) {
              dispatch(
                updateUiState({
                  key: 'mailboxUnrepliedCount',
                  value: response?.data?.unrepliedEmails || 0,
                }),
              );
              resolve(response.data);
            } else {
              reject(rejectWithValue('Failed to fetch interaction counts'));
            }
          } catch (e) {
            reject(rejectWithValue('Failed to fetch interaction counts'));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

const orgSlice = createSlice({
  name: 'org',
  initialState,
  extraReducers: (builder) => {
    // --- Team Members ---
    builder
      .addCase(fetchTeamMembers.pending, (state, action) => {
        const { lastSeen } = action.meta.arg;
        if (lastSeen) {
          state.teamMembers.fetching = false;
          state.teamMembers.fetchingPagination = true;
        } else {
          state.teamMembers.fetching = true;
          state.teamMembers.fetchingPagination = false;
        }
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        const { data, lastSeen, responseDataLastSeen } = action.payload;

        state.teamMembers.fetching = false;
        state.teamMembers.fetchingPagination = false;
        state.teamMembers.fetchingSuccessful = true;
        state.teamMembers.fetchingFailed = false;
        state.teamMembers.error = false;
        state.teamMembers.errorText = '';

        if (lastSeen) {
          state.teamMembers.members = Array.isArray(data)
            ? state.teamMembers.members
              ? state.teamMembers.members.concat(data)
              : data
            : state.teamMembers.members;

          state.teamMembers.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 25 > 0);
        } else {
          state.teamMembers.members = data;
          state.teamMembers.emptyList = false;
        }

        state.teamMembers.lastSeen =
          responseDataLastSeen || state.teamMembers.lastSeen;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        const { lastSeen } = action.meta.arg;
        state.teamMembers.fetching = false;
        if (lastSeen) {
          state.teamMembers.fetchingPagination = false;
        } else {
          state.teamMembers.fetchingSuccessful = false;
          state.teamMembers.fetchingPagination = false;
          state.teamMembers.fetchingFailed = true;
          state.teamMembers.error = true;
          state.teamMembers.errorText = action.payload || 'Failed';
          state.teamMembers.members = [];
          state.teamMembers.emptyList = false;
          state.teamMembers.lastSeen = '';
        }
      });

    // --- Invitations ---
    builder
      .addCase(fetchInvitations.pending, (state, action) => {
        const { lastSeen } = action.meta.arg;
        if (lastSeen) {
          state.invitations.fetching = false;
          state.invitations.fetchingPagination = true;
        } else {
          state.invitations.fetching = true;
          state.invitations.fetchingPagination = false;
        }
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        const { data, lastSeenArg, responseDataLastSeen } = action.payload;

        state.invitations.fetching = false;
        state.invitations.fetchingPagination = false;
        state.invitations.fetchingSuccessful = true;
        state.invitations.fetchingFailed = false;
        state.invitations.error = false;
        state.invitations.errorText = '';

        if (lastSeenArg) {
          state.invitations.invitations = Array.isArray(data)
            ? state.invitations.invitations
              ? state.invitations.invitations.concat(data)
              : data
            : state.invitations.invitations;

          state.invitations.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 25 > 0);
        } else {
          state.invitations.invitations = data;
          state.invitations.emptyList = false;
        }

        state.invitations.lastSeen =
          responseDataLastSeen || state.invitations.lastSeen;
      })
      .addCase(fetchInvitations.rejected, (state, action) => {
        const { lastSeen } = action.meta.arg;
        state.invitations.fetching = false;
        if (lastSeen) {
          state.invitations.fetchingPagination = false;
        } else {
          state.invitations.fetchingSuccessful = false;
          state.invitations.fetchingPagination = false;
          state.invitations.fetchingFailed = true;
          state.invitations.error = true;
          state.invitations.errorText = action.payload || 'Failed';
          state.invitations.invitations = [];
          state.invitations.emptyList = false;
          state.invitations.lastSeen = '';
        }
      });

    // --- Org Configs ---
    builder
      .addCase(fetchOrganizationConfigs.pending, (state) => {
        state.orgConfigs.fetching = true;
      })
      .addCase(fetchOrganizationConfigs.fulfilled, (state, action) => {
        state.orgConfigs.fetching = false;
        state.orgConfigs.fetchingSuccessful = true;
        state.orgConfigs.fetchingFailed = false;
        state.orgConfigs.error = false;
        state.orgConfigs.errorText = '';
        state.orgConfigs.orgConfigs = action.payload;
      })
      .addCase(fetchOrganizationConfigs.rejected, (state, action) => {
        state.orgConfigs.fetching = false;
        state.orgConfigs.fetchingFailed = true;
        state.orgConfigs.error = true;
        state.orgConfigs.errorText = action.payload || 'Failed';
        state.orgConfigs.orgConfigs = null;
      });

    // --- Teams ---
    builder
      .addCase(fetchTeams.pending, (state, action) => {
        const { lastSeen } = action.meta.arg;
        if (lastSeen) {
          state.teams.fetching = false;
          state.teams.fetchingPagination = true;
        } else {
          state.teams.fetching = true;
          state.teams.fetchingPagination = false;
        }
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        const { data, lastSeenArg, responseDataLastSeen } = action.payload;

        state.teams.fetching = false;
        state.teams.fetchingPagination = false;
        state.teams.fetchingSuccessful = true;
        state.teams.fetchingFailed = false;
        state.teams.error = false;
        state.teams.errorText = '';

        if (lastSeenArg) {
          state.teams.teams = Array.isArray(data)
            ? state.teams.teams
              ? state.teams.teams.concat(data)
              : data
            : state.teams.teams;

          state.teams.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 10 > 0);
        } else {
          state.teams.teams = data;
          state.teams.emptyList = false;
        }

        state.teams.lastSeen = responseDataLastSeen || state.teams.lastSeen;
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        const { lastSeen } = action.meta.arg;
        state.teams.fetching = false;
        if (lastSeen) {
          state.teams.fetchingPagination = false;
        } else {
          state.teams.fetchingSuccessful = false;
          state.teams.fetchingPagination = false;
          state.teams.fetchingFailed = true;
          state.teams.error = true;
          state.teams.errorText = action.payload || 'Failed';
          state.teams.teams = [];
          state.teams.emptyList = false;
          state.teams.lastSeen = '';
        }
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        if (state.teams.teams) {
          state.teams.teams = state.teams.teams.map((team) => {
            if (team?.id === id) {
              return { ...team, ...data };
            }
            return team;
          });
        }
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        const id = action.payload;
        if (state.teams.teams) {
          state.teams.teams = state.teams.teams.filter(
            (team) => team?.id !== id,
          );
        }
      });

    // --- Seller Participants ---
    builder
      .addCase(fetchOrgSellerParticipants.pending, (state) => {
        state.sellerParticipants.fetching = true;
        state.sellerParticipants.fetchingFailed = false;
      })
      .addCase(fetchOrgSellerParticipants.fulfilled, (state, action) => {
        state.sellerParticipants.fetching = false;
        state.sellerParticipants.fetchingFailed = false;
        state.sellerParticipants.data = action.payload || [];
      })
      .addCase(fetchOrgSellerParticipants.rejected, (state) => {
        state.sellerParticipants.fetching = false;
        state.sellerParticipants.fetchingFailed = true;
      });

    // --- Add/Remove Team Member ---
    builder
      .addCase(addRemoveTeamMember.pending, (state) => {
        state.addRemoveTeamMember.processing = true;
        state.addRemoveTeamMember.done = false;
        state.addRemoveTeamMember.failed = false;
      })
      .addCase(addRemoveTeamMember.fulfilled, (state) => {
        state.addRemoveTeamMember.processing = false;
        state.addRemoveTeamMember.done = true;
        state.addRemoveTeamMember.failed = false;
      })
      .addCase(addRemoveTeamMember.rejected, (state, action) => {
        state.addRemoveTeamMember.processing = false;
        state.addRemoveTeamMember.done = false;
        state.addRemoveTeamMember.failed = true;
        state.addRemoveTeamMember.errorText = action.payload?.text || null;
      });

    // --- Update Team Permissions ---
    builder
      .addCase(updateTeamPermissions.pending, (state) => {
        state.updateTeamPermissions.processing = true;
        state.updateTeamPermissions.done = false;
        state.updateTeamPermissions.failed = false;
      })
      .addCase(updateTeamPermissions.fulfilled, (state) => {
        state.updateTeamPermissions.processing = false;
        state.updateTeamPermissions.done = true;
        state.updateTeamPermissions.failed = false;
      })
      .addCase(updateTeamPermissions.rejected, (state, action) => {
        state.updateTeamPermissions.processing = false;
        state.updateTeamPermissions.done = false;
        state.updateTeamPermissions.failed = true;
        state.updateTeamPermissions.errorText = action.payload?.text || null;
      });

    // --- Send Team Invite ---
    builder
      .addCase(sendTeamInvite.pending, (state) => {
        state.sendTeamInvite.processing = true;
        state.sendTeamInvite.done = false;
        state.sendTeamInvite.failed = false;
      })
      .addCase(sendTeamInvite.fulfilled, (state) => {
        state.sendTeamInvite.processing = false;
        state.sendTeamInvite.done = true;
        state.sendTeamInvite.failed = false;
      })
      .addCase(sendTeamInvite.rejected, (state, action) => {
        state.sendTeamInvite.processing = false;
        state.sendTeamInvite.done = false;
        state.sendTeamInvite.failed = true;
        state.sendTeamInvite.errorText = action.payload?.text || null;
      });

    // --- Create/Delete/Update Org Seller ---
    builder
      .addCase(createOrgSellerParticipant.pending, (state) => {
        state.createOrgSeller.processing = true;
        state.createOrgSeller.done = false;
        state.createOrgSeller.failed = false;
      })
      .addCase(createOrgSellerParticipant.fulfilled, (state) => {
        state.createOrgSeller.processing = false;
        state.createOrgSeller.done = true;
        state.createOrgSeller.failed = false;
      })
      .addCase(createOrgSellerParticipant.rejected, (state) => {
        state.createOrgSeller.processing = false;
        state.createOrgSeller.done = false;
        state.createOrgSeller.failed = true;
      });

    builder
      .addCase(deleteOrgSellerParticipant.pending, (state) => {
        state.deleteOrgSeller.processing = true;
        state.deleteOrgSeller.done = false;
        state.deleteOrgSeller.failed = false;
      })
      .addCase(deleteOrgSellerParticipant.fulfilled, (state) => {
        state.deleteOrgSeller.processing = false;
        state.deleteOrgSeller.done = true;
        state.deleteOrgSeller.failed = false;
      })
      .addCase(deleteOrgSellerParticipant.rejected, (state) => {
        state.deleteOrgSeller.processing = false;
        state.deleteOrgSeller.done = false;
        state.deleteOrgSeller.failed = true;
      });

    builder
      .addCase(updateOrgSellerParticipant.pending, (state) => {
        state.updateOrgSeller.processing = true;
        state.updateOrgSeller.done = false;
        state.updateOrgSeller.failed = false;
      })
      .addCase(updateOrgSellerParticipant.fulfilled, (state) => {
        state.updateOrgSeller.processing = false;
        state.updateOrgSeller.done = true;
        state.updateOrgSeller.failed = false;
      })
      .addCase(updateOrgSellerParticipant.rejected, (state) => {
        state.updateOrgSeller.processing = false;
        state.updateOrgSeller.done = false;
        state.updateOrgSeller.failed = true;
      });

    // --- Update Org Config ---
    builder
      .addCase(updateOrgConfig.pending, (state) => {
        state.updateOrgConfig.processing = true;
        state.updateOrgConfig.done = false;
        state.updateOrgConfig.failed = false;
      })
      .addCase(updateOrgConfig.fulfilled, (state) => {
        state.updateOrgConfig.processing = false;
        state.updateOrgConfig.done = true;
        state.updateOrgConfig.failed = false;
      })
      .addCase(updateOrgConfig.rejected, (state) => {
        state.updateOrgConfig.processing = false;
        state.updateOrgConfig.done = false;
        state.updateOrgConfig.failed = true;
      });

    // --- CRM User Mapping ---
    builder
      .addCase(mapCrmUser.pending, (state) => {
        state.mapCrmUser.processing = true;
        state.mapCrmUser.done = false;
        state.mapCrmUser.failed = false;
      })
      .addCase(mapCrmUser.fulfilled, (state) => {
        state.mapCrmUser.processing = false;
        state.mapCrmUser.done = true;
        state.mapCrmUser.failed = false;
      })
      .addCase(mapCrmUser.rejected, (state) => {
        state.mapCrmUser.processing = false;
        state.mapCrmUser.done = false;
        state.mapCrmUser.failed = true;
      });

    builder
      .addCase(mapHubspotUser.pending, (state) => {
        state.mapHubspotUser.processing = true;
        state.mapHubspotUser.done = false;
        state.mapHubspotUser.failed = false;
      })
      .addCase(mapHubspotUser.fulfilled, (state) => {
        state.mapHubspotUser.processing = false;
        state.mapHubspotUser.done = true;
        state.mapHubspotUser.failed = false;
      })
      .addCase(mapHubspotUser.rejected, (state) => {
        state.mapHubspotUser.processing = false;
        state.mapHubspotUser.done = false;
        state.mapHubspotUser.failed = true;
      });

    builder
      .addCase(mapZohoUser.pending, (state) => {
        state.mapZohoUser.processing = true;
        state.mapZohoUser.done = false;
        state.mapZohoUser.failed = false;
      })
      .addCase(mapZohoUser.fulfilled, (state) => {
        state.mapZohoUser.processing = false;
        state.mapZohoUser.done = true;
        state.mapZohoUser.failed = false;
      })
      .addCase(mapZohoUser.rejected, (state) => {
        state.mapZohoUser.processing = false;
        state.mapZohoUser.done = false;
        state.mapZohoUser.failed = true;
      });

    // --- CRM Data ---
    builder
      .addCase(updateTrackedCrmDeals.pending, (state) => {
        state.updateTrackedCrmDeals.processing = true;
        state.updateTrackedCrmDeals.done = false;
        state.updateTrackedCrmDeals.failed = false;
      })
      .addCase(updateTrackedCrmDeals.fulfilled, (state) => {
        state.updateTrackedCrmDeals.processing = false;
        state.updateTrackedCrmDeals.done = true;
        state.updateTrackedCrmDeals.failed = false;
      })
      .addCase(updateTrackedCrmDeals.rejected, (state) => {
        state.updateTrackedCrmDeals.processing = false;
        state.updateTrackedCrmDeals.done = false;
        state.updateTrackedCrmDeals.failed = true;
      });

    builder
      .addCase(fetchCrmDealContacts.pending, (state) => {
        state.crmDealContacts.fetching = true;
        state.crmDealContacts.fetchingSuccessful = false;
        state.crmDealContacts.fetchingFailed = false;
      })
      .addCase(fetchCrmDealContacts.fulfilled, (state, action) => {
        state.crmDealContacts.fetching = false;
        state.crmDealContacts.fetchingSuccessful = true;
        state.crmDealContacts.fetchingFailed = false;
        state.crmDealContacts.data = action.payload;
      })
      .addCase(fetchCrmDealContacts.rejected, (state) => {
        state.crmDealContacts.fetching = false;
        state.crmDealContacts.fetchingSuccessful = false;
        state.crmDealContacts.fetchingFailed = true;
      });

    builder
      .addCase(fetchCrmDeals.pending, (state) => {
        state.crmDeals.fetching = true;
        state.crmDeals.fetchingSuccessful = false;
        state.crmDeals.fetchingFailed = false;
      })
      .addCase(fetchCrmDeals.fulfilled, (state, action) => {
        state.crmDeals.fetching = false;
        state.crmDeals.fetchingSuccessful = true;
        state.crmDeals.fetchingFailed = false;
        state.crmDeals.data = action.payload;
      })
      .addCase(fetchCrmDeals.rejected, (state) => {
        state.crmDeals.fetching = false;
        state.crmDeals.fetchingSuccessful = false;
        state.crmDeals.fetchingFailed = true;
      });

    builder
      .addCase(fetchCrmDealsV2.pending, (state) => {
        state.crmDealsV2.fetching = true;
        state.crmDealsV2.fetchingSuccessful = false;
        state.crmDealsV2.fetchingFailed = false;
      })
      .addCase(fetchCrmDealsV2.fulfilled, (state, action) => {
        state.crmDealsV2.fetching = false;
        state.crmDealsV2.fetchingSuccessful = true;
        state.crmDealsV2.fetchingFailed = false;
        state.crmDealsV2.data = action.payload;
      })
      .addCase(fetchCrmDealsV2.rejected, (state) => {
        state.crmDealsV2.fetching = false;
        state.crmDealsV2.fetchingSuccessful = false;
        state.crmDealsV2.fetchingFailed = true;
      });

    builder
      .addCase(fetchCrmProperties.pending, (state) => {
        state.crmProperties.fetching = true;
        state.crmProperties.fetchingSuccessful = false;
        state.crmProperties.fetchingFailed = false;
      })
      .addCase(fetchCrmProperties.fulfilled, (state, action) => {
        state.crmProperties.fetching = false;
        state.crmProperties.fetchingSuccessful = true;
        state.crmProperties.fetchingFailed = false;
        state.crmProperties.data = action.payload;
      })
      .addCase(fetchCrmProperties.rejected, (state) => {
        state.crmProperties.fetching = false;
        state.crmProperties.fetchingSuccessful = false;
        state.crmProperties.fetchingFailed = true;
      });

    builder
      .addCase(fetchCrmUsers.pending, (state) => {
        state.crmUsers.fetching = true;
        state.crmUsers.fetchingSuccessful = false;
        state.crmUsers.fetchingFailed = false;
      })
      .addCase(fetchCrmUsers.fulfilled, (state, action) => {
        state.crmUsers.fetching = false;
        state.crmUsers.fetchingSuccessful = true;
        state.crmUsers.fetchingFailed = false;
        state.crmUsers.data = action.payload;
      })
      .addCase(fetchCrmUsers.rejected, (state) => {
        state.crmUsers.fetching = false;
        state.crmUsers.fetchingSuccessful = false;
        state.crmUsers.fetchingFailed = true;
      });

    builder
      .addCase(fetchHubspotCompanies.pending, (state) => {
        state.hubspotCompanies.fetching = true;
        state.hubspotCompanies.fetchingSuccessful = false;
        state.hubspotCompanies.fetchingFailed = false;
      })
      .addCase(fetchHubspotCompanies.fulfilled, (state, action) => {
        state.hubspotCompanies.fetching = false;
        state.hubspotCompanies.fetchingSuccessful = true;
        state.hubspotCompanies.fetchingFailed = false;
        state.hubspotCompanies.data = action.payload;
      })
      .addCase(fetchHubspotCompanies.rejected, (state) => {
        state.hubspotCompanies.fetching = false;
        state.hubspotCompanies.fetchingSuccessful = false;
        state.hubspotCompanies.fetchingFailed = true;
      });

    builder
      .addCase(fetchHubspotUsers.pending, (state) => {
        state.hubspotUsers.fetching = true;
        state.hubspotUsers.fetchingSuccessful = false;
        state.hubspotUsers.fetchingFailed = false;
      })
      .addCase(fetchHubspotUsers.fulfilled, (state, action) => {
        state.hubspotUsers.fetching = false;
        state.hubspotUsers.fetchingSuccessful = true;
        state.hubspotUsers.fetchingFailed = false;
        state.hubspotUsers.data = action.payload;
      })
      .addCase(fetchHubspotUsers.rejected, (state) => {
        state.hubspotUsers.fetching = false;
        state.hubspotUsers.fetchingSuccessful = false;
        state.hubspotUsers.fetchingFailed = true;
      });

    builder
      .addCase(fetchZohoUsers.pending, (state) => {
        state.zohoUsers.fetching = true;
        state.zohoUsers.fetchingSuccessful = false;
        state.zohoUsers.fetchingFailed = false;
      })
      .addCase(fetchZohoUsers.fulfilled, (state, action) => {
        state.zohoUsers.fetching = false;
        state.zohoUsers.fetchingSuccessful = true;
        state.zohoUsers.fetchingFailed = false;
        state.zohoUsers.data = action.payload;
      })
      .addCase(fetchZohoUsers.rejected, (state) => {
        state.zohoUsers.fetching = false;
        state.zohoUsers.fetchingSuccessful = false;
        state.zohoUsers.fetchingFailed = true;
      });

    builder
      .addCase(updateCrmDeal.pending, (state) => {
        state.updateCrmDeal.processing = true;
        state.updateCrmDeal.done = false;
        state.updateCrmDeal.failed = false;
      })
      .addCase(updateCrmDeal.fulfilled, (state) => {
        state.updateCrmDeal.processing = false;
        state.updateCrmDeal.done = true;
        state.updateCrmDeal.failed = false;
      })
      .addCase(updateCrmDeal.rejected, (state) => {
        state.updateCrmDeal.processing = false;
        state.updateCrmDeal.done = false;
        state.updateCrmDeal.failed = true;
      });

    // --- Interaction Counts ---
    builder
      .addCase(getInteractionCounts.pending, (state) => {
        state.interactionCounts.fetching = true;
        state.interactionCounts.fetchingSuccessful = false;
        state.interactionCounts.fetchingFailed = false;
      })
      .addCase(getInteractionCounts.fulfilled, (state, action) => {
        state.interactionCounts.fetching = false;
        state.interactionCounts.fetchingSuccessful = true;
        state.interactionCounts.fetchingFailed = false;
        state.interactionCounts.data = action.payload;
      })
      .addCase(getInteractionCounts.rejected, (state) => {
        state.interactionCounts.fetching = false;
        state.interactionCounts.fetchingSuccessful = false;
        state.interactionCounts.fetchingFailed = true;
      });

    // --- Org Data ---
    builder
      .addCase(fetchOrgData.pending, (state) => {
        state.orgData.fetching = true;
        state.orgData.fetchingSuccessful = false;
        state.orgData.fetchingFailed = false;
      })
      .addCase(fetchOrgData.fulfilled, (state, action) => {
        state.orgData.fetching = false;
        state.orgData.fetchingSuccessful = true;
        state.orgData.fetchingFailed = false;
        state.orgData.data = action.payload;
      })
      .addCase(fetchOrgData.rejected, (state, action) => {
        state.orgData.fetching = false;
        state.orgData.fetchingSuccessful = false;
        state.orgData.fetchingFailed = true;
        state.orgData.errorText = action.payload || 'Failed';
      });
  },
});

export default orgSlice.reducer;
